/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './related-data-chart.component.html';

import {Downloader} from '../../../components/util/downloader';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {RELATED_CHART_DATA} from './related-data-chart-config';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_STAT_TYPES} from '../options/chart-options';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {UserProfileResource} from '../../../components/api/resources/resource/user-profile.resource';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';

@Component({
  standalone: false,
  selector: 'adn-related-data-chart',
  template
})
export class RelatedDataChartComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  @Input() obj: any;
  @Input() objName: string;
  @Input() key: string;
  @Input() chartType: string;
  @Input() dateSelectorId: string;
  @Input() relatedDataType: string;

  pieError: any = null;
  relatedChartData: any;
  dateOption: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  statType = ADN_CHART_STAT_TYPES.impression;
  allStats: any = {};
  totals: any;
  isSpecialUser = false;
  osEmpty = false;
  mobileBrandEmpty = false;
  locationsEmpty = false;
  domainsEmpty = false;
  appEmpty = false;
  isPending = false;

  readonly changeHook = {
    chartChangeHandlers: [] as Array<(totals: any) => void>,
    getTotals: () => this.totals
  };

  get resolvedChartType(): string {
    return this.chartType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get resolvedRelatedDataType(): string {
    return this.relatedDataType;
  }

  private params: Record<string, any> = {};
  private canceller: any;
  private lastRequestKey = '';
  private setupKey = '';

  constructor(
    private readonly statsResource: StatsResource,
    private readonly ngZone: NgZone,
    private readonly adnLog: AdnLogService,
    private readonly translate: TranslateService,
    @Inject(UserProfileResource) private readonly UserProfile: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.ensureStatsLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.obj || changes.key || changes.chartType
      || changes.relatedDataType || changes.dateSelectorId) {
      this.setupKey = '';
      this.lastRequestKey = '';
    }

    this.ensureStatsLoaded();
  }

  ngDoCheck(): void {
    this.ensureStatsLoaded();
  }

  ngOnDestroy(): void {
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
  }

  changeCallback(
    dateOptionId: string,
    startDate: any,
    endDate: any,
    countTypeId: string,
    statTypeId: string
  ): void {
    const chartOptions = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    const localUser = this.localUserProfile.get();
    const profile = localUser.profile;

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[dateOptionId];
    if (_.get(this.dateOption, 'id') === 'useDates' && this.obj?.startDate && this.obj?.endDate) {
      this.params.startDate = moment(this.obj.startDate);
      this.params.endDate = moment(this.obj.endDate);
    } else {
      this.params.startDate = startDate ? moment(startDate) : startDate;
      this.params.endDate = endDate ? moment(endDate) : endDate;
    }
    this.countType = ADN_CHART_COUNT_TYPES[countTypeId];
    this.statType = ADN_CHART_STAT_TYPES[statTypeId];
    this.lastRequestKey = '';
    this.ensureStatsLoaded();

    profile[chartOptions.perfChartOptions] = _.assign({}, profile[chartOptions.perfChartOptions], {
      dateOptionId: _.get(this.dateOption, 'id'),
      startDate: this.params.startDate ? this.params.startDate.toDate() : null,
      endDate: this.params.endDate ? this.params.endDate.toDate() : null,
      countTypeId: this.countType.id,
      statTypeId: this.statType.id
    });

    this.UserProfile.save({
      id: localUser.userId,
      profile: _.pick(profile, chartOptions.perfChartOptions)
    }, (apiUser: any) => {
      this.localUserProfile.safeSaveProfile(chartOptions.perfChartOptions, apiUser.profile[chartOptions.perfChartOptions]);
    });
  }

  save(): void {
    const refData = _.keyBy(_.map(this.allStats.chunks, (chunk) => chunk), 'chunkStart');

    let dataFields: Array<{name: string; id: string; groupId?: string}> = [{name: 'Date', id: 'chunkStart'}];
    _.forEach(this.relatedChartData.dataTypes, (dataType: any) => {
      dataFields = dataFields.concat(_.map(this.allStats.totals[dataType.id], (d, k) => {
        return {
          name: dataType.keyed ? this.translate.instant(dataType.keyed + '.' + k) || k : k,
          id: k,
          groupId: dataType.id
        };
      }));
    });
    const headingFields = _.map(dataFields, 'name');
    const fields = _.map(dataFields, 'id');
    const rows: any[] = [];

    _.forEach(this.allStats.chunkRange, (chunkStart) => {
      const rowData: Record<string, any> = {};
      _.forEach(dataFields, (field) => {
        if (field.id === 'chunkStart') {
          rowData[field.id] = chunkStart;
        } else {
          const refDatum = refData[chunkStart];
          if (refDatum) {
            rowData[field.id] = _.get(refDatum, [field.groupId, field.id]) || 0;
          } else {
            rowData[field.id] = 0;
          }
        }
      });
      rows.push(rowData);
    });

    const date = this.dateOption ? this.translate.instant('chart.dateOption.' + this.dateOption.id) : 'custom dates';
    const defaultName = (this.obj.name + ' ' + this.relatedChartData.csvTitle + ' ' + date + '.xls').replace(/ /g, '-');
    new Downloader().saveXls2(defaultName, headingFields, fields, rows, []);
  }

  private ensureStatsLoaded(): void {
    if (!this.obj?.id || !this.key || !this.resolvedChartType) {
      return;
    }

    const nextSetupKey = [
      this.key,
      this.obj.id,
      this.resolvedChartType,
      this.resolvedRelatedDataType || 'TRAFFIC'
    ].join(':');

    if (nextSetupKey !== this.setupKey) {
      this.setupChartParams();
      this.setupKey = nextSetupKey;
      this.lastRequestKey = '';
    }

    if (!this.params.startDate || !this.params.endDate) {
      return;
    }

    const requestKey = this.buildRequestKey();
    if (requestKey === this.lastRequestKey) {
      return;
    }

    this.lastRequestKey = requestKey;
    this.callForStats();
  }

  private setupChartParams(): void {
    this.pieError = null;
    this.relatedChartData = RELATED_CHART_DATA[this.resolvedRelatedDataType] ? RELATED_CHART_DATA[this.resolvedRelatedDataType] : RELATED_CHART_DATA.TRAFFIC;

    const chartOptions = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    const localUser = this.localUserProfile.get();
    const profile = localUser.profile;
    const profileStartDate = _.get(profile[chartOptions.perfChartOptions], 'startDate');
    const profileEndDate = _.get(profile[chartOptions.perfChartOptions], 'endDate');
    const profileCountType = ADN_CHART_COUNT_TYPES[_.get(profile[chartOptions.perfChartOptions], 'countTypeId')];
    const profileStatType = ADN_CHART_STAT_TYPES[_.get(profile[chartOptions.perfChartOptions], 'statTypeId')];

    this.dateOption = this.ChartOptionsUtil.getDateOptions(this.resolvedChartType)[_.get(profile[chartOptions.perfChartOptions], 'dateOptionId')];
    if (!this.dateOption && (!profileStartDate || !profileEndDate)) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions().last12Hours;
    }
    if (_.get(this.dateOption, 'id') === 'useDates' && this.obj.startDate && this.obj.endDate) {
      this.params.startDate = moment(this.obj.startDate);
      this.params.endDate = moment(this.obj.endDate);
    } else if (this.dateOption) {
      this.params.startDate = this.dateOption.startDate();
      this.params.endDate = this.dateOption.endDate();
    } else if (profileStartDate && profileEndDate) {
      this.params.startDate = moment(profileStartDate);
      this.params.endDate = moment(profileEndDate);
    } else {
      const fallback = this.ChartOptionsUtil.getDateOptions().last12Hours;
      this.dateOption = fallback;
      this.params.startDate = fallback.startDate();
      this.params.endDate = fallback.endDate();
    }
    if (!this.params.startDate || !this.params.endDate
      || !moment(this.params.startDate).isValid() || !moment(this.params.endDate).isValid()) {
      const fallback = this.ChartOptionsUtil.getDateOptions().last12Hours;
      this.dateOption = fallback;
      this.params.startDate = fallback.startDate();
      this.params.endDate = fallback.endDate();
    }
    this.countType = profileCountType ? profileCountType : ADN_CHART_COUNT_TYPES.singular;
    this.statType = profileStatType ? profileStatType : ADN_CHART_STAT_TYPES.impression;

    this.params[this.key] = this.obj.id;
    this.allStats = {};
    this.totals = null;
    this.isSpecialUser = this.localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || this.localUserProfile.get('username') === 'broker1@adnuntius.com';
    this.changeHook.chartChangeHandlers = [];
  }

  private buildRequestKey(): string {
    return [
      this.key,
      this.obj?.id,
      this.resolvedRelatedDataType || 'TRAFFIC',
      this.params.startDate?.valueOf(),
      this.params.endDate?.valueOf(),
      this.statType?.id,
      this.countType?.id
    ].join(':');
  }

  private callForStats(): void {
    this.params.groupBy = this.ChartOptionsUtil.getChunkSize(this.params.startDate, this.params.endDate, null, true).key;
    this.params.statTypeId = this.statType.id;

    this.statsResource[this.relatedChartData.statsMethod](this.params, (data: any) => {
      this.ngZone.run(() => {
        this.allStats.chunks = data.chunks;
        this.allStats.chunkRange = data.chunkRange;
        this.allStats.totals = data.totals;
        this.totals = data.totals;
        this.pieError = null;

        this.osEmpty = _.isEmpty(data.totals.osType);
        this.mobileBrandEmpty = _.isEmpty(data.totals.mobileBrand);
        this.locationsEmpty = _.isEmpty(data.totals.country);
        this.domainsEmpty = _.isEmpty(data.totals.domain);
        this.appEmpty = _.isEmpty(data.totals.app);

        _.forEach(this.relatedChartData.dataTypes, (dataType: any) => {
          if (dataType.eventHook.redraw) {
            dataType.eventHook.redraw();
          }
        });

        _.forEach(this.changeHook.chartChangeHandlers, (handler) => {
          handler(this.totals);
        });
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.pieError = err;
        this.adnLog.warn(err);
      });
    }, this);
  }
}
